import React, { useState } from "react"
import { useDispatch } from "react-redux"
import * as Yup from "yup"
import { Form, Formik } from "formik"
import { v4 as uuidv4 } from "uuid"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import CheckIcon from "@mui/icons-material/Check"
import { addContact, updateContact } from "../../../../actions/Organizations/Enterprise"
import { openAppMessageSnackBar } from "../../../../actions/Utils/app"
import { COLORS } from "../../../../utils"

const CONTACT_ROLES = ["buyer", "direction", "operations", "billing", "other"]

const ROLE_LABELS = {
  buyer: "Acheteur",
  direction: "Direction",
  operations: "Exploitation",
  billing: "Facturation",
  other: "Autre",
}

const getRoleLabel = (role) => ROLE_LABELS[role] || role

const ROLE_TONES = [
  { bg: "#ECECEC", color: "#424242" },
  { bg: "#E3F2FD", color: "#1045A1" },
  { bg: "#F1E7FF", color: "#5B21B6" },
  { bg: "#E6F9F4", color: "#1F7A66" },
  { bg: "#FFF4E0", color: "#8A5A00" },
  { bg: "#FFE5E9", color: "#A8123A" },
  { bg: "#E1F0FA", color: "#155E75" },
  { bg: "#F4E9DA", color: "#6B4423" },
]

const getRoleTone = (role) => {
  if (!role) return ROLE_TONES[0]
  const normalized = getRoleLabel(role).toLowerCase().trim()
  let hash = 0
  for (let i = 0; i < normalized.length; i++) hash = (hash * 31 + normalized.charCodeAt(i)) >>> 0
  return ROLE_TONES[hash % ROLE_TONES.length]
}

const FIELD_DEFS = [
  { name: "firstName", label: "Prénom", type: "text", maxLength: 100 },
  { name: "lastName", label: "Nom", type: "text", maxLength: 100 },
  { name: "email", label: "Email", type: "email" },
  { name: "role", label: "Rôle", type: "role" },
  { name: "phone", label: "Téléphone (optionnel)", type: "text", maxLength: 14, placeholder: "06 12 34 56 78" },
]

const cleanPhone = (value) => (value || "").replace(/[\s.-]/g, "")

const getPhoneError = (value) => {
  const cleaned = cleanPhone(value)
  if (!cleaned) return null
  if (/^(?:\+33|00)/.test(cleaned)) return "Saisissez le numéro au format français (ex. 01 23 45 67 89)"
  if (/[^0-9]/.test(cleaned)) return "Le numéro ne doit contenir que des chiffres"
  if (cleaned[0] !== "0") return "Un numéro français commence par 0 (ex. 01 23 45 67 89)"
  if (cleaned.length !== 10) return "Le numéro doit contenir 10 chiffres (ex. 01 23 45 67 89)"
  return null
}

const buildValidationSchema = (existingEmails) => Yup.object({
  firstName: Yup.string().trim().max(100, "100 caractères max").required("Le prénom est requis"),
  lastName: Yup.string().trim().max(100, "100 caractères max").required("Le nom est requis"),
  email: Yup.string().trim().email("Email invalide").required("L'email est requis")
    .test("not-duplicate", "Cet email est déjà utilisé", (value) =>
      !value || !existingEmails.includes(value.trim().toLowerCase())
    ),
  role: Yup.string().required("Le rôle est requis"),
  phone: Yup.string().test("phone-format", function (value) {
    const error = getPhoneError(value)
    return error ? this.createError({ message: error }) : true
  }),
})

const RoleChip = ({ role }) => {
  const tone = getRoleTone(role)
  return (
    <Box component="span" sx={{ ...sx.roleChip, bgcolor: tone.bg, color: tone.color }}>
      {getRoleLabel(role)}
    </Box>
  )
}

const FieldLabel = ({ children, focused, error }) => (
  <Typography sx={{ ...sx.fieldLabel, ...(error ? sx.fieldLabelError : focused ? sx.fieldLabelFocused : {}) }}>
    {children}
  </Typography>
)

const RoleSelect = ({ formik, setFocusedField, hasError }) => {
  const { values, handleChange, handleBlur, errors } = formik
  return (
    <>
      <Select
        fullWidth
        variant="standard"
        name="role"
        value={values.role}
        onChange={handleChange}
        onFocus={() => setFocusedField("role")}
        onBlur={(e) => { setFocusedField(null); handleBlur(e) }}
        error={hasError}
        displayEmpty
        renderValue={(selected) => selected
          ? <RoleChip role={selected} />
          : <Typography component="span" sx={sx.rolePlaceholder}>Sélectionner un rôle</Typography>
        }
      >
        {CONTACT_ROLES.map((role) => (
          <MenuItem key={role} value={role} sx={sx.roleMenuItem}>
            <RoleChip role={role} />
            {values.role === role && <CheckIcon sx={sx.roleCheck} />}
          </MenuItem>
        ))}
      </Select>
      {hasError && <FormHelperText error>{errors.role}</FormHelperText>}
    </>
  )
}

const FormField = ({ field, formik, focusedField, setFocusedField }) => {
  const { name, label } = field
  const { values, errors, touched, handleChange, handleBlur } = formik
  const hasError = touched[name] && Boolean(errors[name])

  return (
    <Box>
      <FieldLabel focused={focusedField === name} error={hasError}>
        {label}
      </FieldLabel>
      {field.type === "role" ? (
        <RoleSelect formik={formik} setFocusedField={setFocusedField} hasError={hasError} />
      ) : (
        <TextField
          fullWidth
          variant="standard"
          name={name}
          type={field.type}
          placeholder={field.placeholder}
          value={values[name]}
          onChange={handleChange}
          onFocus={() => setFocusedField(name)}
          onBlur={(e) => { setFocusedField(null); handleBlur(e) }}
          inputProps={field.maxLength ? { maxLength: field.maxLength } : undefined}
          error={hasError}
          helperText={touched[name] && errors[name]}
        />
      )}
    </Box>
  )
}

const ContactFormModal = ({ open, onClose, enterpriseId, contact, existingEmails }) => {
  const dispatch = useDispatch()
  const [serverError, setServerError] = useState(null)
  const [focusedField, setFocusedField] = useState(null)
  const isEdit = Boolean(contact)

  const initialValues = {
    firstName: contact?.firstName || "",
    lastName: contact?.lastName || "",
    email: contact?.email || "",
    role: contact?.role || "",
    phone: contact?.phone || "",
  }

  const normalizedExistingEmails = existingEmails.map((e) => (e || "").trim().toLowerCase())

  const handleSubmit = async (values, { setSubmitting }) => {
    setServerError(null)
    try {
      const payload = {
        id: isEdit ? contact.id : uuidv4(),
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        role: values.role,
        phone: cleanPhone(values.phone) || null,
      }
      await dispatch(isEdit ? updateContact(enterpriseId, payload) : addContact(enterpriseId, payload))
      onClose()
      const fullName = [payload.firstName, payload.lastName].filter(Boolean).join(" ")
      dispatch(openAppMessageSnackBar(`Contact « ${fullName} » ${isEdit ? "mis à jour" : "ajouté"}.`))
    } catch {
      setServerError("Une erreur est survenue, veuillez réessayer")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} sx={sx.dialog}>
      <Formik
        initialValues={initialValues}
        validationSchema={buildValidationSchema(normalizedExistingEmails)}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => (
          <Form>
            <DialogTitle sx={sx.title}>
              {isEdit ? "Modifier le contact" : "Ajouter un contact"}
              <IconButton onClick={onClose} disabled={formik.isSubmitting} sx={sx.closeBtn}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={sx.content}>
              <Stack spacing={3}>
                {FIELD_DEFS.map((field) => (
                  <FormField
                    key={field.name}
                    field={field}
                    formik={formik}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                ))}
              </Stack>
            </DialogContent>

            {serverError && (
              <Typography sx={sx.serverError}>{serverError}</Typography>
            )}

            <DialogActions sx={sx.actions}>
              <Button variant="text" onClick={onClose} disabled={formik.isSubmitting}>
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={formik.isSubmitting}
                startIcon={formik.isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {formik.isSubmitting
                  ? (isEdit ? "Enregistrement…" : "Ajout…")
                  : (isEdit ? "Enregistrer" : "Ajouter")}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

const sx = {
  dialog: {
    "& .MuiDialog-paper": {
      width: 560,
      maxWidth: "calc(100vw - 32px)",
      borderRadius: "8px",
    },
  },
  title: {
    px: 5,
    pt: 3,
    pb: 1,
    fontSize: 18,
    fontWeight: 500,
    pr: 8,
    marginBottom: "20px !important",
  },
  closeBtn: {
    position: "absolute",
    right: 16,
    top: 16,
    color: "text.secondary",
  },
  content: {
    px: 5,
    pt: 1,
    pb: 2,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 400,
    color: COLORS.DARK_GREY,
    mb: 1,
    transition: "color 160ms",
  },
  fieldLabelFocused: {
    color: "primary.main",
  },
  fieldLabelError: {
    color: "error.main",
  },
  serverError: {
    px: 5,
    pb: 1,
    fontSize: 14,
    color: "error.main",
  },
  actions: {
    px: 5,
    pt: 3,
    pb: 4,
    justifyContent: "space-between"
  },
  roleChip: {
    display: "inline-flex",
    alignItems: "center",
    px: "10px",
    py: "4px",
    fontSize: 12,
    fontWeight: 500,
    borderRadius: "999px",
    lineHeight: 1.4,
  },
  rolePlaceholder: {
    fontSize: 14,
    color: "text.disabled",
  },
  roleMenuItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  roleCheck: {
    fontSize: 18,
    color: "primary.main",
  },
}

export default ContactFormModal
